/**
 * The plugin's manifest, as the workspace edits it.
 *
 * THE DRAFT IS THE WHOLE MANIFEST, LOADED FROM `GET /plugins/<id>`. The old
 * builder rebuilt a body from the handful of fields it showed, so saving a plugin
 * with four tools sent none of them and the backend cleared them. Here a save
 * sends only what changed: a key the developer never touched is not in the body,
 * and the backend keeps what it has. That is also what makes a save with no edits
 * a save of nothing (no version, no re-approval).
 *
 * The words here are the ones a person sees in Nucleus (the design's Words):
 * Reads / Writes / Outside ofSelf / Data goes to, Can't be undone, Ask every run,
 * Settings / Inputs.
 */

export const KINDS = [
  { id: 'endpoint', label: 'Endpoint' },
  { id: 'mcp', label: 'MCP server' },
  { id: 'request', label: 'Request' },
];

export const kindLabel = (t) => (KINDS.find((k) => k.id === (t || 'endpoint')) || { label: t }).label;

export const VERBS = [
  { id: 'create', label: 'Creates' },
  { id: 'edit', label: 'Edits' },
  { id: 'delete', label: 'Deletes' },
  { id: 'propose', label: 'Proposes' },
];

/* What a tool does outside ofSelf. The sentences are the backend's own
   (plugins_pkg/effects.py KINDS), so the preview reads as Turn on does. */
export const EFFECT_KINDS = [
  { id: 'send', label: 'Sends', text: 'sends something to someone — a message, an email, a post' },
  { id: 'modify', label: 'Changes', text: 'changes something in an outside system' },
  { id: 'delete', label: 'Removes', text: 'removes something from an outside system' },
  { id: 'transact', label: 'Pays', text: 'moves money or commits to an obligation' },
  { id: 'other', label: 'Other', text: 'has some other effect outside your graph' },
];

/** Removing and paying can never be undone, whatever the author says. */
export const ALWAYS_IRREVERSIBLE = ['delete', 'transact'];

export const irreversible = (effect) => !effect?.reversible || ALWAYS_IRREVERSIBLE.includes(effect?.kind);

/* ── equality ─────────────────────────────────────────────────────────────── */

/** Deep equality over JSON values. Key order does not matter; `undefined` keys
    are treated as absent, the way JSON.stringify sends them. */
export function same(a, b) {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => same(x, b[i]));
  }
  const ka = Object.keys(a).filter((k) => a[k] !== undefined);
  const kb = Object.keys(b).filter((k) => b[k] !== undefined);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && same(a[k], b[k]));
}

/* ── the draft ────────────────────────────────────────────────────────────── */

/**
 * The editable manifest from a `GET /plugins/<id>` body.
 *
 * `oauth_provider` is left undefined: the GET does not return it, so the draft
 * cannot know it, and an untouched one is never sent. Setting it to an object
 * (or to null, to disconnect) is an edit like any other.
 */
export function draftFromPlugin(p = {}) {
  return {
    name: p.name || '',
    description: p.description || '',
    usage_guide: p.usage_guide || '',
    execution_type: p.execution_type || 'endpoint',
    endpoint_url: p.plugin_endpoint_url || '',
    plugin_endpoint_base_url: p.plugin_endpoint_base_url || '',
    mcp_server_url: p.mcp_server_url || '',
    request_spec: p.request_spec ?? null,
    response_map: p.response_map ?? null,
    input_parameters: p.input_parameters || [],
    node_requirements: p.node_requirements || [],
    output_declaration: p.output_declaration || [],
    effects: p.effects || [],
    tools: p.tools || [],
    enc_public_key: p.enc_public_key || '',
    requires_consent: !!p.requires_consent,
    remove_source: !!p.remove_source,
    oauth_provider: undefined,
  };
}

// Strings where an empty value means "none" on the platform.
const NULL_WHEN_EMPTY = ['endpoint_url', 'plugin_endpoint_base_url', 'mcp_server_url', 'enc_public_key'];

/**
 * The PUT body: only what differs from `base` (the draft the plugin loaded as).
 *
 * `execution_type` is sent only when the developer changed it, so an MCP or
 * request plugin is never quietly turned into an endpoint (audit bug 4). The
 * endpoint goes as `endpoint_url`, the key the backend reads (audit bug 1).
 */
export function bodyFromDraft(draft, base) {
  const body = {};
  for (const key of Object.keys(draft)) {
    if (key === 'oauth_provider') continue;
    if (same(draft[key], base[key])) continue;
    let value = draft[key];
    if (typeof value === 'string') value = key === 'name' ? value.trim() : value;
    if (NULL_WHEN_EMPTY.includes(key) && !String(value || '').trim()) value = null;
    else if (NULL_WHEN_EMPTY.includes(key)) value = String(value).trim();
    if (key === 'effects' && Array.isArray(value) && !value.length) value = null;
    body[key] = value;
  }
  if (draft.oauth_provider !== undefined) body.oauth_provider = draft.oauth_provider;
  return body;
}

export const isEmptyBody = (body) => !body || Object.keys(body).length === 0;

/* ── schema references ────────────────────────────────────────────────────── */

/** A write, with its schema picked by name and stored by id (audit bug 2). A
    changed id drops the old `schema_name`, which the backend fills in again for
    a create; a stale one would name the wrong schema. */
export function withSchema(entry, schemaId) {
  const next = { ...entry };
  delete next.schema_name;
  delete next.schema;
  if (schemaId) next.schema_id = schemaId;
  else delete next.schema_id;
  return next;
}

/** A read of one schema, or of any node. Other keys (min) are kept. */
export function readOf(entry, schemaId) {
  const next = { ...entry };
  delete next.schema_name;
  if (schemaId) {
    next.type = 'schema';
    next.schema_id = schemaId;
  } else {
    next.type = 'any';
    delete next.schema_id;
  }
  return next;
}

/* ── where it runs ────────────────────────────────────────────────────────── */

export function hostOf(url) {
  try {
    return new URL(String(url)).host || null;
  } catch {
    return null;
  }
}

/** Where a tool's run goes: the hosts a person reads under "Data goes to". */
export function destinations(draft, tool) {
  const t = draft.execution_type || 'endpoint';
  if (t === 'request') return [hostOf(draft.request_spec?.url)].filter(Boolean);
  if (t === 'mcp') return [hostOf(tool?.mcp_server_url || draft.mcp_server_url)].filter(Boolean);
  const url = tool?.endpoint_path && draft.plugin_endpoint_base_url
    ? draft.plugin_endpoint_base_url + tool.endpoint_path
    : (draft.endpoint_url || draft.plugin_endpoint_base_url);
  return [hostOf(url)].filter(Boolean);
}

/* ── what Turn on shows a person, when the backend can't say ────────────── */

/**
 * `{parts:[…]}` in the shape of `GET /plugins/<id>/asks-for`, built from the
 * draft. The backend's own words are used whenever it answers; this is only for
 * a backend that does not have the route yet. The texts follow consent_record's.
 */
export function localAsksFor(draft, schemaName = () => null) {
  const name = (id) => (id ? schemaName(id) || 'a schema that no longer exists' : null);
  const reads = (reqs) => {
    const out = [];
    for (const r of reqs || []) {
      const text = r.type === 'schema' ? name(r.schema_id)
        : r.type === 'any' ? 'any node' : r.plugin_name ? `what ${r.plugin_name} makes` : 'another plugin\'s nodes';
      if (text && !out.includes(text)) out.push(text);
    }
    return out;
  };
  const writes = (decls) => {
    const out = [];
    for (const d of decls || []) {
      if (!d?.action) continue;
      const n = d.schema_id ? name(d.schema_id) : null;
      const text = n ? `${d.action} ${n}` : d.action;
      if (!out.includes(text)) out.push(text);
    }
    return out;
  };
  const outside = (effects) => (effects || []).map((e) => {
    const k = EFFECT_KINDS.find((x) => x.id === e.kind);
    const base = String(e.description || '').trim() || k?.text || e.kind;
    return irreversible(e) ? `${base} · cannot be undone` : base;
  });
  const part = (toolId, label, src, tool) => {
    const effects = src.effects || [];
    return {
      tool_id: toolId,
      name: label,
      irreversible: effects.some(irreversible),
      reads: reads(src.node_requirements),
      writes: writes(src.output_declaration),
      outside: outside(effects),
      goes_to: destinations(draft, tool),
      removes_source: !!draft.remove_source,
      asks_every_run: !!draft.requires_consent,
    };
  };
  const tools = draft.tools || [];
  if (!tools.length) return { parts: [part(null, draft.name || 'This plugin', draft, null)] };
  return {
    parts: tools.map((t) => part(t.tool_id, t.name || t.tool_id, {
      node_requirements: t.node_requirements ?? draft.node_requirements,
      output_declaration: t.output_declaration ?? draft.output_declaration,
      effects: t.effects ?? draft.effects,
    }, t)),
  };
}

/* ── errors at their fields ───────────────────────────────────────────────── */

/** The field a backend message is about: `tools[0].effects[1] needs …` →
    `tools[0].effects[1]`. Null when the message doesn't start with a path. */
export function fieldOfMessage(message) {
  const m = /^([a-z_]+(?:\[\d+\])?(?:\.[a-z_]+(?:\[\d+\])?)*)(?![\w[.])/.exec(String(message || ''));
  if (!m) return null;
  const path = m[1];
  // A sentence that starts with an ordinary word is not a path.
  return /[[._]/.test(path) || KNOWN_FIELDS.includes(path) ? path : null;
}

const KNOWN_FIELDS = ['name', 'description', 'tools', 'effects', 'input_parameters', 'node_requirements',
  'output_declaration', 'request_spec', 'response_map', 'oauth_provider', 'execution_type'];

/** Normalise errors from changes-preview (`[{field, message}]`) or a refused PUT
    (`message`) to `[{field, message}]`. */
export function toErrors(source) {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.map((e) => (typeof e === 'string'
      ? { field: fieldOfMessage(e), message: e }
      : { field: e.field || fieldOfMessage(e.message), message: e.message || String(e) }));
  }
  const message = String(source.message || source);
  return [{ field: fieldOfMessage(message), message }];
}

/** The errors at `prefix` or beneath it (`tools[2]` also matches `tools[2].effects[0]`). */
export function errorsAt(errors, prefix) {
  return (errors || []).filter((e) => e.field && (e.field === prefix
    || e.field.startsWith(`${prefix}.`) || e.field.startsWith(`${prefix}[`)));
}

/* ── how far along each step is ───────────────────────────────────────────── */

/** What a tool still needs: plain sentences, empty when it is done. */
export function toolGaps(tool, draft) {
  const gaps = [];
  if (!String(tool.name || '').trim()) gaps.push('A name');
  if (!String(tool.description || '').trim()) gaps.push('A description');
  if (draft?.execution_type === 'mcp' && tool.tool_id !== undefined && !tool.mcp_tool_name) {
    gaps.push('The server\'s name for it');
  }
  for (const e of tool.effects || []) {
    if (!String(e.description || '').trim()) { gaps.push('A sentence for what it does outside ofSelf'); break; }
  }
  return gaps;
}

export function detailsGaps(draft) {
  const gaps = [];
  if (!draft.name.trim()) gaps.push('A name');
  if (!draft.description.trim()) gaps.push('A description');
  const t = draft.execution_type;
  if (t === 'endpoint' && !draft.endpoint_url.trim() && !draft.plugin_endpoint_base_url.trim()) gaps.push('Where it runs');
  if (t === 'mcp' && !draft.mcp_server_url.trim()) gaps.push('The MCP server');
  if (t === 'request' && !draft.request_spec?.url) gaps.push('The request');
  if (t === 'request' && !draft.response_map?.external_id) gaps.push('What makes each item unique');
  return gaps;
}

export function toolsGaps(draft) {
  const tools = draft.tools || [];
  // A plugin with no tools is one tool: its name and description are Details',
  // so only what it does outside ofSelf is counted here.
  const n = tools.length
    ? tools.reduce((sum, t) => sum + toolGaps(t, draft).length, 0)
    : ((draft.effects || []).some((e) => !String(e.description || '').trim()) ? 1 : 0);
  const secrets = [...(draft.input_parameters || []), ...tools.flatMap((t) => t.input_parameters || [])]
    .some((f) => f.secret);
  return n + (secrets && draft.execution_type === 'endpoint' && !draft.enc_public_key ? 1 : 0);
}

/** `{id: {status, done}}` for the step column. */
export function stepStates(plugin, draft, { dirty } = {}) {
  const left = (n) => (n ? `${n} left` : 'Done');
  const d = detailsGaps(draft).length;
  const t = toolsGaps(draft);
  const v = plugin?.verification?.state || (plugin?.is_verified ? 'verified' : 'not_requested');
  const pub = (plugin?.deployment_mode || 'incubator') === 'public';
  return {
    details: { status: left(d), done: !d },
    tools: { status: left(t), done: !t },
    preview: { status: dirty ? 'Unsaved edits' : 'Up to date', done: !dirty },
    test: { status: 'Test run', done: false },
    verification: {
      status: v === 'verified' ? 'Verified' : v === 'requested' ? 'Requested' : 'Not requested',
      done: v === 'verified',
    },
    publish: { status: pub ? 'Public' : 'Incubator', done: pub },
    runs: { status: 'Your runs', done: false },
    settings: { status: 'Signing secret · versions', done: false },
  };
}

/* ── field types, in words ────────────────────────────────────────────────── */

export const TYPE_WORDS = {
  boolean: 'On or off',
  text: 'Short text',
  textarea: 'Long text',
  integer: 'Whole number',
  float: 'Number',
  number: 'Number',
  date: 'Date',
  datetime: 'Date and time',
  select: 'One of a list',
  multiselect: 'Several from a list',
  node: 'Something in their graph',
  file: 'A file',
};

export const typeWords = (t) => TYPE_WORDS[t] || t;

// One of them, as its tag reads: a Setting (asked once) or an Input (asked each run).
export const SCOPE_WORDS = { setup: 'Setting', runtime: 'Input' };
